import { IJxtNavbar } from '@jooxter/ui';
import { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PROFILE_ROUTE, TOGETHER_ROUTE } from '@jooxter/core';
import { getLandingRoute, getMainNav } from './helpers';
import { getEnvVar } from '@jooxter/env';
import { usePermissions } from '@jooxter/permissions';

export const useBaseNavigation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { booking, meetup } = permissions;
  // The logo: the home page with the booking capability (ND-6414, ND-6444), Spaces otherwise.
  const baseRoute = getLandingRoute(booking);
  const mainNav = getMainNav(permissions, t);
  const baseNavigation: { routes: IJxtNavbar['routes']; buttons?: (JSX.Element | null)[]; baseRoute: string } = useMemo(
    () => ({
      routes: {
        main: mainNav,
        secondary: [
          {
            name: t('navbar.profile'),
            icon: 'user',
            to: PROFILE_ROUTE,
          },
        ],
      },
      baseRoute: baseRoute,
      buttons: [
        meetup ? (
          <button
            onClick={() => navigate(TOGETHER_ROUTE)}
            key={t('together.beta')}
            className="bg-primary-100 text-white px-4.25 lg:-mr-px py-3 max-lg:mx-4 rounded-lg font-['Open_Sans'] text-[14px] font-semibold hover:bg-[#78acff] transition duration-300"
          >
            {t('together.beta')}
          </button>
        ) : null,
        <p key="jimo-badge" className="jimo-widget flex items-center h-10 max-lg:w-10 lg:px-1 px-4 lg:mx-1">
          <i className="fas fa-gift opacity-60 fa-xl -mt-0.5 text-neutral-100 " />
        </p>,
        <a
          key={t('navbar.help')}
          target="_blank"
          rel="noopener noreferrer"
          title={t<string>('navbar.help')}
          href={t<string>('help-center-url')}
          className="h-10 lg:w-10 max-lg:mx-4 flex items-center justify-center"
        >
          <i className="fas fa-question-circle opacity-60 fa-xl text-neutral-100" />
        </a>,
      ],
    }),
    [mainNav, baseRoute, t, navigate, meetup]
  );

  const devBaseNavigation: { routes: IJxtNavbar['routes']; buttons?: (JSX.Element | null)[]; baseRoute: string } =
    useMemo(
      () => ({
        routes: {
          main: mainNav,
          secondary: [
            {
              name: t('navbar.profile'),
              icon: 'user',
              to: PROFILE_ROUTE,
            },
          ],
        },
        baseRoute: baseRoute,
        buttons: [
          meetup ? (
            <button
              onClick={() => navigate(TOGETHER_ROUTE)}
              key={t('together.beta')}
              className="bg-primary-100 text-white px-4.25 lg:-mr-px py-3 max-lg:mx-4 rounded-lg font-['Open_Sans'] text-[14px] font-semibold hover:bg-[#78acff] transition duration-300"
            >
              {t('together.beta')}
            </button>
          ) : null,
          <p key="jimo-badge" className="jimo-widget flex items-center h-10 max-lg:w-10 lg:px-1 px-4 lg:mx-1">
            <i className="fas fa-gift opacity-60 fa-xl -mt-0.5 text-neutral-100 " />
          </p>,
          <a
            key={t('navbar.help')}
            target="_blank"
            rel="noopener noreferrer"
            title={t<string>('navbar.help')}
            href={t<string>('help-center-url')}
            className="h-10 lg:w-10 max-lg:mx-4 flex items-center justify-center"
          >
            <i className="fas fa-question-circle opacity-60 fa-xl text-neutral-100" />
          </a>,
        ],
      }),
      [mainNav, baseRoute, t, navigate, meetup]
    );

  return getEnvVar('DEV') ? [devBaseNavigation] : [baseNavigation];
};
